import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useActiveLocale } from "./LocaleContext";
import { instanceFor } from "./instances";
import { DEFAULT_LOCALE, liveLocale, type LocaleDef, type Market } from "./locales";

/**
 * The fallback `LocaleDef` for renders outside a `:lang` subtree (shell/sandbox).
 * Resolved once at module load. `DEFAULT_LOCALE` being a live locale is a routing
 * invariant (server.js 301s legacy paths to it, the prerender matrix assumes it),
 * so a missing entry is a build-time misconfiguration — fail loud here rather than
 * crash later with an opaque "cannot read 'code' of undefined" deep in a hook.
 */
const DEFAULT_LOCALE_DEF: LocaleDef = (() => {
  const def = liveLocale(DEFAULT_LOCALE);
  if (!def) {
    throw new Error(
      `useLocale: DEFAULT_LOCALE "${DEFAULT_LOCALE}" is not a live locale — ` +
        `it must exist in LIVE_LOCALES (check locales.ts).`,
    );
  }
  return def;
})();

/**
 * `useLocale()` (Story 4.2, AC3) — the canonical per-render locale accessor that
 * Stories 4.3–4.9 build on. Returns the active locale's identity plus a bound
 * `t` and the (LTR-only, §0) text direction.
 *
 * Surface is intentionally stable: `{ locale, htmlLang, market, t, dir }`.
 */
export interface LocaleInfo {
  /** Active locale / i18next language key (e.g. "nl", "en"). */
  locale: string;
  /** Value for `<html lang>` (wired to the path in Story 4.5). */
  htmlLang: string;
  /** Market derived from the locale's registry field ("nl"/"fr-be" → belgium, "en" → international). */
  market: Market;
  /** Translation function bound to the active locale's fixed instance. */
  t: TFunction;
  /** Text direction — literal "ltr" (the site is LTR-only per §0). */
  dir: "ltr";
}

/**
 * Resolves the active `LocaleDef` from `LocaleContext`. Below `LocaleLayout`
 * (every `/:lang/…` page) this is the path's locale. ABOVE it — the shell
 * (`SiteNav`/`Footer`/`ChatWidget`, mounted in `Layout`) and the sandbox/demo
 * routes that sit outside `:lang` — `useActiveLocale()` is `null`, so we fall
 * back to `DEFAULT_LOCALE` (nl → belgium), preserving today's belgium-default
 * behaviour and matching the `force="belgium"` demo mounts (NFR9). `Layout`
 * (Story 4.1) already provides `LocaleContext` for the shell from `useParams`.
 *
 * `t` is bound to the locale's fixed instance EXPLICITLY (not via the ambient
 * react-i18next global) so it stays correct even outside an `<I18nextProvider>`
 * and never depends on a mutable singleton (NFR6).
 */
export function useLocale(): LocaleInfo {
  const def = useActiveLocale() ?? DEFAULT_LOCALE_DEF;
  const { t } = useTranslation(undefined, { i18n: instanceFor(def.code) });
  return { locale: def.code, htmlLang: def.htmlLang, market: def.market, t, dir: "ltr" };
}
